import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PatientMiniProfile from '../../PatientMiniProfile/PatientMiniProfile';
import Select from '../../Base/Select/Select';
import DatePicker from '../../Base/DatePicker/DatePicker';
import TimePicker from '../../Base/TimePicker/TimePicker';
import TextArea from '../../Base/TextArea/TextArea';
import apiClient, { IResponse } from '../../../Services/ApiClient';
import FormControl from '../../Form/FormControl';
import { useAppContext } from '../../../Services/AppContext';
import Form from '../../Form/Form';
import SubmitButton from '../../Button/SubmitButton';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';
import * as yup from 'yup';
import Text from '../../Base/Text/Text';
import PatientTimeZoneInfo, {
	PatientTimeZoneInfoValues,
} from '../../Patient/PatientTimeZoneInfo';
import { useEffectAsync } from '../../../helpers';
import useTranslation from '../../../Hooks/useTranslation';
import useHealthPlanAppointment from '../../../Modules/HealthPlan/Services/healthPlanAppointment.service';
import FormWrapper from '../../Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { HealthPlanCode } from '../../../Enums/HealthPlanCode.enum';
// @ts-ignore
import styles from '../../Calendar/CalendarPatientEvent.module.scss';
import Checkbox from '../../Base/Checkbox/Checkbox';
import { useIsLatam } from '../../../Modules/Region/Services/region.service';
import { getMomentDate } from '../../../Utils/dates.util';
import { updateHealthPlanUserItem } from '../../../Modules/HealthPlan/Services/healthPlan.service';

interface IAppointmentDetail {
	selectedDate: any;
	patientId?: string;
	onSuccess?: () => void;
	workplaceId?: string;
	loggedUserId?: string;
	order?: any;
	params?: any;
	onEventIsCreated?: (response: IResponse) => void;
}

const AppointmentDetail: React.FC<IAppointmentDetail> = props => {
	const {
		selectedDate,
		patientId,
		workplaceId,
		loggedUserId,
		params,
		onSuccess,
		onEventIsCreated,
		order,
	} = props;
	const { translation, globalTemplate, userData } = useAppContext();
	const [meetingTypePick, setMeetingTypePick] = useState(
		params.allowPickMeetingType,
	);
	const { healthPlanUser, healthPlan, activeHealthPlanUserItem } =
		useHealthPlanContext();
	const [selectedPatientId, setSelectedPatientId] = useState(patientId);
	const [timeFrom, setTimeFrom] = useState(null);
	const [timeTo, setTimeTo] = useState(null);
	const [usersTimeDiff, setUsersTimeDiff] = useState(null);
	const [patientTimezone, setPatientTimezone] = useState(null);
	const { getDateRangeByMonth } = useHealthPlanAppointment();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const isLatam = useIsLatam(globalTemplate);

	const [defaultValues, setDefaultValues] = useState({
		date: selectedDate,
		timeFrom: selectedDate,
		meetingType:
			activeHealthPlanUserItem?.extra?.data?.meetingType === 'onSite'
				? { value: 'onSite', label: translation['base.meetingType.onSite'] }
				: {
						value: 'telemedicine',
						label: translation['base.meetingType.telemedicine'],
				  },
		paidMeeting: activeHealthPlanUserItem?.extra?.data?.paidMeeting ?? false,
	});

	const validationSchema = yup.object({
		meetingType: yup
			.mixed()
			.required(translation['validation.calendarPatientEvent.meetingType']),
		date: yup
			.date()
			.required(translation['validation.calendarPatientEvent.date']),
		timeFrom: yup
			.date()
			.nullable()
			.default(undefined)
			.required(translation['validation.calendarPatientEvent.timeFrom']),
		timeTo: yup
			.date()
			.nullable()
			.default(undefined)
			.required(translation['validation.calendarPatientEvent.timeTo']),
		note: yup.string().notRequired(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		// resolver: yupResolver(validationSchema),
	});

	// @ts-ignore
	const handleSetPatientTimezone = () => {
		if (!formMethods) {
			return;
		}

		const formValues = formMethods.getValues();
		setSelectedPatientId(patientId ?? formValues?.patientId?.value);
		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	// @ts-ignore
	useEffectAsync(async () => {
		if (patientId) {
			await handleSetPatientTimezone();
		}
	}, []);

	const handleSave = async (values: any) => {
		setIsLoading(true);

		const d = {
			date: values.date.format('DD'),
			month: parseInt(values.date.format('MM')) - 1,
			year: values.date.format('YYYY'),
		};

		let dataToPatch: { [key: string]: any } = {
			note: values.note,
			date: values.date.toDate(),
			start: values.timeFrom.set(d).toDate(),
			end: values.timeTo.set(d).toDate(),
			paidMeeting: values.paidMeeting,
		};

		if (meetingTypePick) {
			dataToPatch['meetingType'] = values.meetingType?.value;
		}

		if (healthPlanUser?.healthPlanId && activeHealthPlanUserItem?.id) {
			const result = await updateHealthPlanUserItem({
				healthPlanId: healthPlanUser.healthPlanId,
				healthPlanUserId: healthPlanUser.id,
				healthPlanItemId: activeHealthPlanUserItem.id,
				data: {
					extra: {
						data: dataToPatch,
					},
				},
			});
			if (result) {
				onEventIsCreated?.(result);
				onSuccess?.();
			}
		}
		setIsLoading(false);
	};

	// @ts-ignore
	const handleGetPatientsTimes = () => {
		if (!formMethods) {
			return;
		}

		const formValues = formMethods.getValues();

		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	const handlePatientTimeDiffChange = (change: PatientTimeZoneInfoValues) => {
		setUsersTimeDiff(change.timeDiff);
		setPatientTimezone(change.patientTimezone);
	};

	useEffect(() => {
		if (!order) {
			return;
		}

		const tempDefaultValues: any = {
			date: order?.event?.start ? getMomentDate(order.event.start) : undefined,
			timeFrom: order?.event?.start
				? getMomentDate(order.event.start)
				: undefined,
			timeTo: order?.event?.end ? getMomentDate(order.event.end) : undefined,
			note: order?.event?.extendedProps?.note,
		};

		if (order) {
			if (order?.event?.extendedProps?.meta) {
				const meetingTypeMeta = order.event.extendedProps.meta.find(
					m => m.key === 'meetingType',
				);

				if (meetingTypeMeta) {
					switch (meetingTypeMeta.value) {
						case 'telemedicine':
							tempDefaultValues['meetingType'] = {
								value: 'telemedicine',
								label: t('base.meetingType.telemedicine'),
							};
							break;
						case 'onSite':
							tempDefaultValues['meetingType'] = {
								value: 'onSite',
								label: t('base.meetingType.onSite'),
							};
							break;
					}
				}
			}
		}

		setTimeFrom(tempDefaultValues.timeFrom);
		setTimeTo(tempDefaultValues.timeTo);
		setDefaultValues(tempDefaultValues);

		formMethods.reset(tempDefaultValues);
	}, [order]);

	return (
		<>
			{patientId && (
				<div className={'mb-4'}>
					<PatientMiniProfile
						userId={patientId}
						workplaceId={workplaceId}
						loggedUserId={loggedUserId}
						showVerifyInformation={false}
						links={null}
						showAge={true}
						showBirthDate={false}
						showAddress={false}
						showPhone={false}
						showEmail={false}
						showInsuranceCompany={false}
						showGender={false}
					/>
				</div>
			)}
			{healthPlan.code === HealthPlanCode.DIABETES && (
				<div className={styles.plannedIn}>
					{t('component.appointmentDetail.meetingHasToBePlannedIn', {
						month: getDateRangeByMonth(
							healthPlanUser?.activeFrom
								? moment(healthPlanUser?.activeFrom)
								: moment(),
							activeHealthPlanUserItem?.extra?.month,
						),
					})}
				</div>
			)}
			{activeHealthPlanUserItem?.extra?.data?.meetingType &&
				!meetingTypePick && (
					<div
						style={{
							fontWeight: '500',
							fontSize: '14px',
							marginBottom: '20px',
							color: '#1d65d8',
						}}>
						<Text tx={'component.eventCalendar.typeOfEvent'} inline={true} />
						<Text tx={': '} inline={true} />
						{activeHealthPlanUserItem?.extra?.data?.meetingType ===
							'onSite' && <Text tx={'base.meetingType.onSite'} inline={true} />}
						{activeHealthPlanUserItem?.extra?.data?.meetingType ===
							'telemedicine' && (
							<Text tx={'base.meetingType.telemedicine'} inline={true} />
						)}
					</div>
				)}
			<FormWrapper formMethods={formMethods}>
				{meetingTypePick && (
					<FormControl
						label={'component.eventCalendar.typeOfEvent'}
						name='meetingType'
						isRequired={true}>
						<Select
							placeholder={translation['base.shared.pick']}
							options={[
								{
									value: 'telemedicine',
									label: translation['base.meetingType.telemedicine'],
								},
								{
									value: 'onSite',
									label: translation['base.meetingType.onSite'],
								},
							]}
						/>
					</FormControl>
				)}
				{!!usersTimeDiff && (
					<div className={styles.warning}>
						<Text tx={'base.patientsIsInTimeZone.part1'} inline firstUpper /> (
						{patientTimezone}){', '}
						<span className={styles.warningBold}>{usersTimeDiff}</span>{' '}
						<Text
							tx={'base.shared.hourMiddle'}
							className={styles.warningBold}
							inline
						/>{' '}
						<Text tx={'base.patientsIsInTimeZone.part2'} inline />.
					</div>
				)}
				<div className={'row'}>
					<div className={'col-4'}>
						<FormControl label='base.shared.date' name='date' isRequired={true}>
							<DatePicker minDate={moment()} />
						</FormControl>
					</div>
					<div className={'col-4'}>
						<FormControl
							label='base.shared.from'
							name='timeFrom'
							isRequired={true}
							onChange={handleGetPatientsTimes}>
							<TimePicker />
						</FormControl>
						<PatientTimeZoneInfo
							patientId={selectedPatientId}
							time={timeFrom}
							onChange={handlePatientTimeDiffChange}
						/>
					</div>
					<div className={'col-4'}>
						<FormControl
							label='base.shared.to'
							name='timeTo'
							isRequired={true}
							onChange={handleGetPatientsTimes}>
							<TimePicker />
						</FormControl>
						<PatientTimeZoneInfo
							patientId={selectedPatientId}
							time={timeTo}
							onChange={handlePatientTimeDiffChange}
						/>
					</div>
				</div>
				<FormControl label={'base.shared.noteVisibleForPatient'} name='note'>
					<TextArea />
				</FormControl>
				{isLatam && (
					<FormControl name='paidMeeting'>
						<Checkbox tx='component.calendarPatientEvent.paidMeeting' />
					</FormControl>
				)}
				<div className={'mt-4'}>
					<SubmitButton
						title={'component.eventDetail.saveToCalendarAndSendInfoToPatient'}
						onSubmit={handleSave}
						disabled={isLoading}
					/>
				</div>
			</FormWrapper>
		</>
	);
};

export default AppointmentDetail;
